// src/pages/Commissions/CommissionsPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import { getGroupsListCommissions } from "../../services/groupServices";
import { countCommissionArticles } from "../../services/articlescommissionsServices";
import { ToastContainer, toast } from "react-toastify";
import ScrollIndicator from "../../hooks/ScrollIndicator";
import { useUser } from "../../contexts/UserContext";
import useScrollToTop from "../../hooks/useScrollToTop";
import InfoBubble from "./../../components/InfoBubble/InfoBubble";
import CommissionCard from "./CommissionCard";
import { FaSearch } from "react-icons/fa";
import PageHeader from "../../components/Layout/PageHeader";

const normalize = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const CommissionsPage = () => {
  useScrollToTop();
  const { user } = useUser(); // gardé si tu veux gérer des droits plus tard

  const [groups, setGroups] = useState([]);
  const [articlesCounts, setArticlesCounts] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchCommissions = async () => {
      try {
        const [groupsData, articlesCountsData] = await Promise.all([
          getGroupsListCommissions(),
          countCommissionArticles(),
        ]);

        // Tri alphabétique par nom de commission
        const sortedGroups = [...groupsData].sort((a, b) =>
          (a.nom || "").localeCompare(b.nom || "", "fr", {
            sensitivity: "base",
          })
        );

        setGroups(sortedGroups);

        const counts = {};
        articlesCountsData.forEach((item) => {
          counts[item.group_id] = item.group_count;
        });
        setArticlesCounts(counts);
      } catch (error) {
        console.error(error);
        toast.error(
          "Erreur lors de la récupération des commissions ou des articles"
        );
      }
    };

    fetchCommissions();
  }, []);

  const filteredGroups = useMemo(() => {
    const term = normalize(searchTerm);
    if (!term) return groups;
    return groups.filter((group) => normalize(group.nom).includes(term));
  }, [groups, searchTerm]);

  return (
    <div className="min-h-screen flex flex-col w-full mx-auto pt-[130px] sm:pt-[130px] md:pt-[150px] lg:pt-[150px] xl:pt-[150px] pb-10 bg-slate-50 dark:bg-slate-900 dark:text-white">
      <ScrollIndicator />

      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHeader
          eyebrow="Visiteurs & Utilisateurs"
          title="Les Commissions du Comité"
          subtitle="Découvrez l’organisation du Comité Départemental : missions, responsables et articles publiés par chaque commission tout au long de la saison."
          align="center"
          size="lg"
          className="mb-6 md:mb-8"
        />

        <InfoBubble title="Bienvenue sur la page des Commissions du Comité">
          <p className="text-sm">
            Cliquez sur une commission pour voir sa description, ses
            responsables et accéder aux articles publiés. Vous pouvez filtrer
            par nom de commission avec la barre de recherche.
          </p>
        </InfoBubble>
      </div>

      {/* Barre de recherche + stats */}
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="rounded-2xl bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-700/80 shadow-lg">
          <div className="h-1.5 bg-gradient-to-r from-primary via-indigo-500 to-cyan-400" />
          <div className="p-4 md:p-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="w-full md:w-2/3">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                <input
                  type="text"
                  placeholder="Rechercher une commission par nom..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:text-white"
                />
              </div>
            </div>
            <div className="text-xs md:text-sm text-slate-600 dark:text-slate-300">
              <span className="font-semibold">
                {filteredGroups.length} commission
                {filteredGroups.length > 1 && "s"}
              </span>{" "}
              affichée
              {filteredGroups.length > 1 && "s"} sur{" "}
              <span className="font-semibold">{groups.length}</span>.
            </div>
          </div>
        </div>
      </div>

      {/* Grid des commissions */}
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {filteredGroups.length === 0 ? (
          <div className="mt-10 text-center text-sm text-slate-500 dark:text-slate-300">
            Aucune commission ne correspond à votre recherche.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
            {filteredGroups.map((group) => (
              <CommissionCard
                key={group.id}
                group={group}
                articleCount={articlesCounts[group.id] || 0}
              />
            ))}
          </div>
        )}
      </div>

      <ToastContainer />
    </div>
  );
};

export default CommissionsPage;
